// src/pages/Private/Teacher/TeacherDashboard.js
import { useState, useEffect } from 'react';
import { getMyCourses, getLessonsForTeacher } from '../../../api/teacherApi';
import { Link } from 'react-router-dom';
import './TeacherDashboard.css';

function TeacherDashboard() {
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalLessons: 0,
    totalStudents: 0,
    upcomingLessons: 0,
    pendingQuizzes: 0,
    pendingAssignments: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [coursesData, lessonsData] = await Promise.all([
        getMyCourses(),
        getLessonsForTeacher()
      ]);

      setCourses(coursesData);
      setLessons(lessonsData);
      
      if (coursesData.length > 0) {
        setSelectedCourseId(coursesData[0]._id);
      }

      // Calculate statistics
      calculateStats(coursesData, lessonsData);
    } catch (error) {
      setError('Error loading data: ' + (error.response?.data?.msg || error.message));
      console.error('Dashboard yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (coursesData, lessonsData) => {
    const totalStudents = coursesData.reduce((sum, course) => 
      sum + (course.students?.length || 0), 0
    );

    const upcomingLessons = lessonsData.filter(lesson => 
      new Date(lesson.scheduledAt) > new Date()
    ).length;

    // Pending quizzes and assignments (will come from backend)
    setStats({
      totalCourses: coursesData.length,
      totalLessons: lessonsData.length,
      totalStudents,
      upcomingLessons,
      pendingQuizzes: 3,
      pendingAssignments: 2
    });
  };

  const filterLessonsByCourse = () => {
    if (!selectedCourseId) return [];
    return lessons.filter(lesson => 
      lesson.course && lesson.course._id === selectedCourseId
    );
  };

  const getUpcomingLessons = () => {
    return lessons
      .filter(lesson => new Date(lesson.scheduledAt) > new Date())
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
      .slice(0, 3);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const lessonDate = new Date(dateString);
    const diffMs = lessonDate - now;
    
    if (diffMs <= 0) return 'Started';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins}m`;
    return `${diffMins}m`;
  };

  if (loading) {
    return (
      <div className="teacher-dashboard-loading">
        <div className="loading-spinner">📊</div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const selectedCourse = courses.find(c => c._id === selectedCourseId);
  const courseLessons = filterLessonsByCourse();
  const upcomingLessons = getUpcomingLessons();

  return (
    <div className="teacher-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>👋 Teacher Dashboard</h1>
          <p>Manage your courses and lessons</p>
        </div>
        <button onClick={loadDashboardData} className="refresh-btn" disabled={loading}>
          <span className="refresh-icon">🔄</span>
          Refresh
        </button>
      </div>

      {error && (
        <div className="dashboard-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <h3>Total Courses</h3>
            <div className="stat-value">{stats.totalCourses}</div>
            <div className="stat-change">Your active courses</div>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>Total Lessons</h3>
            <div className="stat-value">{stats.totalLessons}</div>
            <div className="stat-change">Scheduled lessons</div>
          </div>
        </div>
        
        <div className="stat-card warning">
          <div className="stat-icon">👨‍🎓</div>
          <div className="stat-content">
            <h3>Total Students</h3>
            <div className="stat-value">{stats.totalStudents}</div>
            <div className="stat-change">Enrolled students</div>
          </div>
        </div>
        
        <div className="stat-card info">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Upcoming Lessons</h3>
            <div className="stat-value">{stats.upcomingLessons}</div>
            <div className="stat-change">Scheduled lessons</div>
          </div>
        </div>
      </div>

      {/* Pending Tasks */}
      {(stats.pendingQuizzes > 0 || stats.pendingAssignments > 0) && (
        <div className="pending-tasks">
          <h3>📋 Pending Tasks</h3>
          <div className="tasks-grid">
            {stats.pendingQuizzes > 0 && (
              <Link to="/teacher/lessons" className="task-card">
                <span className="task-icon">📝</span>
                <span className="task-count">{stats.pendingQuizzes}</span>
                <span className="task-label">Quizzes to Grade</span>
              </Link>
            )}
            {stats.pendingAssignments > 0 && (
              <Link to="/teacher/lessons" className="task-card">
                <span className="task-icon">📋</span>
                <span className="task-count">{stats.pendingAssignments}</span>
                <span className="task-label">Assignments to Grade</span>
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {/* Left Column - Course Information */}
        <div className="content-column">
          {/* Course Selection */}
          <div className="content-card">
            <h3>🎯 Select Course</h3>
            {courses.length === 0 ? (
              <div className="empty-state">
                <span>📚</span>
                <p>You don't have any courses yet</p>
                <Link to="/teacher/create-course" className="create-link">
                  Create Course
                </Link>
              </div>
            ) : (
              <select 
                value={selectedCourseId} 
                onChange={(e) => setSelectedCourseId(e.target.value)}
                className="course-select"
              >
                {courses.map(course => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Course Details */}
          {selectedCourse && (
            <div className="content-card">
              <h3>📋 Course Details</h3>
              <div className="course-details">
                <div className="detail-item">
                  <span className="detail-label">Course Name:</span>
                  <span className="detail-value">{selectedCourse.title}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Description:</span>
                  <span className="detail-value">{selectedCourse.description}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">{selectedCourse.category || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Student Count:</span>
                  <span className="detail-value">{selectedCourse.students?.length || 0} students</span>
                </div>
                {selectedCourse.zoomLink && (
                  <div className="detail-item">
                    <span className="detail-label">Zoom Link:</span>
                    <a href={selectedCourse.zoomLink} target="_blank" rel="noopener noreferrer" className="zoom-link">
                      Join Class
                    </a>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{formatDate(selectedCourse.createdAt)}</span>
                </div>
                <div className="detail-actions">
                  <Link to={`/teacher/courses/${selectedCourse._id}/modules`} className="action-link">
                    Manage Modules →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Course Lessons */}
          <div className="content-card">
            <h3>📖 Course Lessons ({courseLessons.length})</h3>
            {courseLessons.length === 0 ? (
              <div className="empty-state">
                <span>📭</span>
                <p>No lessons added for this course yet</p>
                <Link to={`/teacher/add-lesson?course=${selectedCourseId}`} className="create-link">
                  Add Lesson
                </Link>
              </div>
            ) : (
              <div className="lessons-list">
                {courseLessons.slice(0, 3).map(lesson => (
                  <div key={lesson._id} className="lesson-item">
                    <div className="lesson-header">
                      <h4>{lesson.title}</h4>
                      <span className="lesson-time">{formatDate(lesson.scheduledAt)}</span>
                    </div>
                    <div className="lesson-details">
                      <span>{lesson.durationMinutes} minutes</span>
                      <div className="lesson-links">
                        {lesson.zoomLink && (
                          <a href={lesson.zoomLink} target="_blank" rel="noopener noreferrer" className="join-link">
                            Join
                          </a>
                        )}
                        {lesson.quiz && (
                          <Link to={`/teacher/quiz/${lesson.quiz}/results`} className="quiz-link">
                            Results
                          </Link>
                        )}
                        {lesson.assignment && (
                          <Link to={`/teacher/assignment/${lesson.assignment}/grade`} className="assignment-link">
                            Grade
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {courseLessons.length > 3 && (
                  <Link to={`/teacher/lessons?course=${selectedCourseId}`} className="view-more">
                    View All →
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Upcoming Lessons and Quick Actions */}
        <div className="content-column">
          <div className="content-card">
            <h3>⏰ Upcoming Lessons</h3>
            {upcomingLessons.length === 0 ? (
              <div className="empty-state">
                <span>📅</span>
                <p>No upcoming lessons</p>
              </div>
            ) : (
              <div className="upcoming-lessons">
                {upcomingLessons.map(lesson => (
                  <div key={lesson._id} className="upcoming-lesson">
                    <div className="lesson-info">
                      <h4>{lesson.title}</h4>
                      <p className="course-name">{lesson.course?.title}</p>
                      <p className="lesson-time">{formatDate(lesson.scheduledAt)}</p>
                    </div>
                    <div className="lesson-actions">
                      <span className="time-until">{getTimeUntil(lesson.scheduledAt)}</span>
                      {lesson.zoomLink && (
                        <a href={lesson.zoomLink} target="_blank" rel="noopener noreferrer" className="join-btn">
                          Join
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="content-card">
            <h3>⚡ Quick Actions</h3>
            <div className="quick-actions">
              <Link to="/teacher/create-course" className="quick-action">
                <span className="action-icon">➕</span>
                <span className="action-text">New Course</span>
              </Link>
              <Link to="/teacher/add-lesson" className="quick-action">
                <span className="action-icon">📖</span>
                <span className="action-text">Add Lesson</span>
              </Link>
              <Link to="/teacher/add-student" className="quick-action">
                <span className="action-icon">👨‍🎓</span>
                <span className="action-text">Add Student</span>
              </Link>
              <Link to="/teacher/lessons" className="quick-action">
                <span className="action-icon">📅</span>
                <span className="action-text">Lesson Schedule</span>
              </Link>
            </div>
          </div>

          {/* Tips */}
          <div className="content-card">
            <h3>💡 Tips</h3>
            <ul className="tips-list">
              <li>Organize your content by adding modules to courses</li>
              <li>Add quizzes or assignments to each lesson</li>
              <li>Regularly check student submissions</li>
              <li>Make learning easier by organizing lessons into modules</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeacherDashboard;